import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FaCamera } from "react-icons/fa";
import { useTranslation } from "react-i18next"; // Import i18n
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function Profile() {
  const { t, i18n } = useTranslation("global"); // Initialize i18n
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedProfile, setUpdatedProfile] = useState({
    name: '',
    email: '',
    birthdate: '',
    image: null,
    imagePreview: '',
    code_student: '',
  });
  const [children, setChildren] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      const userData = JSON.parse(localStorage.getItem("userDataLogin"));
      if (!userData || !userData.token) {
        toast.error(t("messages.unauthenticated"));
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/profile`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${userData.token}`,
              "Accept-Language": i18n.language,  // استخدم اللغة من i18n
            },
          }
        );
        

        const result = await response.json();

        if (response.ok) {
          setProfile(result.data);
          setUpdatedProfile({
            name: result.data.name || "",
            email: result.data.email || "",
            birthdate: result.data.birthdate || "",
            imagePreview: result.data.imageUrl || "",
            code_student: result.data.code_student || "",
          });
          setChildren(result.data.children || []);
          setLoading(false);
        } else {
          toast.error(result.message || t("messages.errorFetchingProfile"));
        }
      } catch (err) {
        toast.error(t("messages.somethingWentWrong"));
      }
    };

    fetchProfile();
  }, [t]);

  const handleChangePassword = async () => {
    const userData = JSON.parse(localStorage.getItem('userDataLogin'));

    if (!newPassword || !confirmPassword) {
      toast.error(t("messages.passwordEmpty"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("messages.passwordMismatch"));
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${userData.token}`,
          },
          body: JSON.stringify({
            email: profile.email,
            password: newPassword,
            password_confirmation: confirmPassword,
          }),
        }
      );
      

      const result = await response.json();

      if (response.ok) {
        toast.success(t("messages.passwordUpdateSuccess"));
        setShowPasswordFields(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(result.message || t("messages.errorUpdateProfile"));
      }
    } catch (err) {
      toast.error(t("messages.somethingWentWrong"));
    }
  };

  const handleProfileUpdate = async () => {
    const userData = JSON.parse(localStorage.getItem("userDataLogin"));

    const formData = new FormData();
    formData.append("name", updatedProfile.name);
    formData.append("birthdate", updatedProfile.birthdate);

    if (updatedProfile.email !== profile.email) {
      formData.append("email", updatedProfile.email);
    }

    if (updatedProfile.image) {
      formData.append("image", updatedProfile.image);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${userData.token}`,
            "Accept-Language": i18n.language,  // استخدم اللغة من i18n
          },
          body: formData,
        }
      );
      

      const result = await response.json();

      if (response.ok) {
        toast.success(t("messages.profileUpdateSuccess"));
        setProfile({
          ...profile,
          name: result.data.name || profile.name,
          email: result.data.email || profile.email,
          image: result.data.image || profile.image,
          birthdate: result.data.birthdate || profile.birthdate,
          code_student: result.data.code_student || profile.code_student,
        });

        setUpdatedProfile({
          ...updatedProfile,
          name: result.data.name || updatedProfile.name,
          email: result.data.email || updatedProfile.email,
          imagePreview: result.data.imageUrl || updatedProfile.imagePreview,
          birthdate: result.data.birthdate || updatedProfile.birthdate,
          code_student: result.data.code_student || updatedProfile.code_student,
        });

        const updatedUserData = {
          ...userData,
          user: {
            ...userData.user,
            name: result.data.name || userData.user.name,
            email: result.data.email || userData.user.email,
            image: result.data.image || userData.user.image,
            birthdate: result.data.birthdate || userData.user.birthdate,
            code_student: result.data.code_student || userData.user.code_student,
          },
        };

        localStorage.setItem("userDataLogin", JSON.stringify(updatedUserData));
      } else {
        toast.error(result.message || t("messages.errorUpdateProfile"));
      }
    } catch (err) {
      toast.error(t("messages.somethingWentWrong"));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setUpdatedProfile({
        ...updatedProfile,
        image: file,
        imagePreview: imageUrl,
      });
    }
  };

  const handleAddStudentCode = async () => {
    const userData = JSON.parse(localStorage.getItem("userDataLogin"));

    const formData = new FormData();
    formData.append("code_student", updatedProfile.code_student);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${userData.token}`,
          },
          body: formData,
        }
      );
      

      const result = await response.json();
      if (response.ok) {
        toast.success(t("messages.successAddStudentCode"), {
          onClose: () => {
            window.location.reload();
          }
        });

        setProfile({
          ...profile,
          code_student: result.data.code_student,
        });
        setUpdatedProfile({
          ...updatedProfile,
          code_student: result.data.code_student,
        });

      } else {
        toast.error(result.message || t("messages.errorAddingStudentCode"));
      }
    } catch (err) {
      toast.error(t("messages.somethingWentWrong"));
    }
  };

  if (loading) return <div>
    {/* Skeleton loading for profile information */}
    <Skeleton circle={true} height={200} width={200} />
    <Skeleton width={200} />
    <Skeleton width={300} />
    <Skeleton count={3} width="100%" />
  </div>;
  if (!profile) return <div>{t("messages.errorFetchingProfile")}</div>;

  return (
    <>
      <ToastContainer />
      <div className="custom-section">
        <div className="container">
          <h1 className="mb-3  ">{t("profile.title")}</h1>
          <h6 className="m-0  ">
            <Link className="link-hover" to="/">{t("profile.breadcrumb")}</Link>
          </h6>
        </div>
      </div>

      <div className="container my-4">
        <div style={{ backgroundColor: "#FCF8F8", borderRadius: "10px" }} className="row p-3">
          <div className="d-flex align-items-center position-relative" style={{ width: '200px' }}>
            <img
              style={{ width: "200px", height: "200px", borderRadius: "50%" }}
              src={profile?.image || "https://static.vecteezy.com/system/resources/thumbnails/026/911/382/small_2x/happy-student-boy-with-books-isolated-free-photo.jpg"}
              alt="Profile"
            />
            <label
              className="position-absolute"
              style={{
                bottom: "10px",
                right: "10px",
                backgroundColor: "#fff",
                borderRadius: "50%",
                padding: "10px",
                cursor: "pointer",
              }}
            >
              <FaCamera />
              <input
                type="file"
                className="d-none"
                accept="image/*"
                onChange={handleImageChange}
              />
            </label>
          </div>

          <div className="col-md-12 mb-2 mt-3">
            <label>{t("profile.name")}</label>
            <input
              type="text"
              className="form-control mt-1 py-2"
              value={updatedProfile.name}
              onChange={(e) => setUpdatedProfile({ ...updatedProfile, name: e.target.value })}
            />
          </div>

          <div className="col-md-12 mb-2">
            <label>{t("profile.email")}</label>
            <input
              type="email"
              className="form-control mt-1 py-2"
              value={updatedProfile.email}
              onChange={(e) => setUpdatedProfile({ ...updatedProfile, email: e.target.value })}
            />
          </div>

          <div className="col-md-12 mb-2">
            <label>{t("profile.birthdate")}</label>
            <input
              type="date"
              className="form-control mt-1 py-2"
              value={updatedProfile.birthdate}
              onChange={(e) => setUpdatedProfile({ ...updatedProfile, birthdate: e.target.value })}
            />
          </div>

          <div className="col-md-12 mb-2">
            <label>{t("profile.studentCode")}</label>
            <input
              type="text"
              className="form-control mt-1 py-2"
              value={updatedProfile.code_student}
              onChange={(e) => setUpdatedProfile({ ...updatedProfile, code_student: e.target.value })}
            />
          </div>

          <button
            className=" my-2 mt-3 mx-auto"
            style={{ color: "black", background: "#E9DFDF", width: '200px', border: "none", padding: "10px", maxWidth: '100%' }}
            onClick={handleAddStudentCode}
          >
            {t("profile.addStudentCode")}
          </button>

          <button
            className="btn w-100 my-2"
            style={{ background: "#0a1056", color: "white" }}
            onClick={handleProfileUpdate}
          >
            {t("profile.saveChanges")}
          </button>

          <div className="mt-4">
            <p
              style={{ textDecoration: "underline", color: "#0d6efd", cursor: "pointer" }}
              onClick={() => setShowPasswordFields(!showPasswordFields)}
            >
              {t("profile.changePassword")}
            </p>
          </div>

          {showPasswordFields && (
            <div className="mt-3">
              <div className="mb-2">
                <label>{t("profile.newPassword")}</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder={t("profile.newPassword")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label>{t("profile.confirmPassword")}</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder={t("profile.confirmPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button
                className="btn my-3 btn-success d-flex justify-content-center mx-auto"
                style={{ color: "white", width: '160px', maxWidth: '100%' }}
                onClick={handleChangePassword}
              >
                {t("profile.savePassword")}
              </button>
            </div>
          )}
        </div>

        <section className="container my-5">
          <h2 className=" fs-4 mb-2">{t("your_children")}</h2>
          <div className="row">
            {profile.student && profile.student.length > 0 ? (
              profile.student.map((child, index) => (
                <div className="col-md-3 mb-4" key={index}>
                  <Link to={`/profileInfo/${child.id}`}>

                    <div className="card custom-card h-100">
                      <img
                        src={child.image || "https://static.vecteezy.com/system/resources/thumbnails/026/911/382/small_2x/happy-student-boy-with-books-isolated-free-photo.jpg"}
                        alt="Student"
                        className="card-img-top"
                      />
                      <div className="card-body">
                        <h5 className="card-title text-center">{child.name}</h5>
                        <p className="paragraph-color mb-1" style={{ fontSize: "16px" }}>
                          <strong className="text-black">{t("grade")}:</strong> {child.grade}
                        </p>
                        <p className="paragraph-color mb-2" style={{ fontSize: "16px" }}>
                          <strong className="text-black">{t("stage")}:</strong> {child.stage}
                        </p>
                      </div>
                      <div className="card-footer text-center mb-2" style={{ borderTop: "1px solid #E9DFDF" }}>
                        <Link to={`/profileInfo/${child.id}`} className="second-color mb-2">
                          {t("profile.showProfile")}
                        </Link>
                      </div>
                    </div>
                  </Link>

                </div>

              ))
            ) : (
              <p>{t("no_children_found")}</p>
            )}
          </div>
        </section>

      </div>
    </>
  );
}
